import React, { useContext, useEffect, useState, useRef } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import Title from '../../components/Title';
import { axiosKK } from '../../services/networkRequest';
import { PreviousOrderListResponse, RemovalOrdersResponse, Order, MyAccountResponse, OxfordPackageOrder, OxfordPackageOrdersResponse, TimeslotListResponse, HasPaymentMethodResponse } from '../../models/APIModels';
import UserContext from '../../contexts/UserContext';
import OrderEditDeliveryDetailsModal from '../../components/edit/OrderEditDeliveryDetailsModal';
import { OrderSelectionModal } from '../../components/OrderSelectionModal';
import { ActiveOrderCard } from '../../components/user/ActiveOrderCard';
import { OrderCardSkeleton } from '../../components/user/OrderCardSkeleton';
import { NextPaymentResponse } from '../../models/APIModels';
import { ExtraServicesModal } from '../../components/modals/ExtraServicesModal';
import { TimeSlotModal } from '../../components/modals/TimeSlotModal';
import { isOrderCompleted } from '../../utilities/OrderHelpers';
import {
  loadDashboardCache,
  saveDashboardCache,
  clearDashboardCache,
  convertPaymentsObjectToMap,
} from '../../utilities/DashboardCache';
import { isEmptyValue } from '../../utilities/ValueHelpers';
import { toMoney } from '../../utilities/MoneyConversion';
import Moment from 'moment';
import useGtmEventTracking from '../../components/useGtmEventTracking';

function DashboardComponent({ history }: RouteComponentProps): JSX.Element {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showOrderSelectionModal, setShowOrderSelectionModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showExtraServicesModal, setShowExtraServicesModal] = useState(false);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<number>>(new Set());
  const [nextPayments, setNextPayments] = useState<Map<number, NextPaymentResponse>>(new Map());
  const [totalBoxDays, setTotalBoxDays] = useState<number | undefined>(undefined);
  const [membershipTier, setMembershipTier] = useState<string | undefined>(undefined);
  const [showPointsInfo, setShowPointsInfo] = useState(false);
  const [oxfordOrders, setOxfordOrders] = useState<OxfordPackageOrder[]>([]);
  const [bookedTimeslotOrderIds, setBookedTimeslotOrderIds] = useState<Set<number>>(new Set());
  const [hasPaymentMethod, setHasPaymentMethod] = useState(true);
  const { user } = useContext(UserContext);
  const gtmEventTracking = useGtmEventTracking();
  const dashboardViewTracked = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    let isMounted = true;
    setIsLoading(true);

    // Try to load from cache first
    const cachedData = loadDashboardCache();
    if (cachedData) {
      setOrders(cachedData.orders);
      setNextPayments(convertPaymentsObjectToMap(cachedData.nextPayments));
      if (cachedData.totalBoxDays !== undefined) {
        setTotalBoxDays(cachedData.totalBoxDays);
      }
      if (cachedData.membershipTier !== undefined) {
        setMembershipTier(cachedData.membershipTier);
      }
      setIsLoading(false);
    }

    // Fetch fresh data in the background
    Promise.all([
      axiosKK.get<PreviousOrderListResponse>('/order/previous'),
      axiosKK.get<RemovalOrdersResponse>('/removals/orders').catch(() => ({ data: { orders: [], nextPayments: {} } })),
      axiosKK.get<MyAccountResponse>('/customer/account'),
      axiosKK.get<OxfordPackageOrdersResponse>('/oxford-packages/orders').catch(() => ({ data: { orders: [], jwt: '' } })),
      axiosKK.get<TimeslotListResponse>('/order/timeslots').catch(() => ({ data: { timeslots: [] } })),
      axiosKK.get<HasPaymentMethodResponse>('/customer/hasPaymentMethod').catch(() => ({ data: { hasPaymentMethod: false } })),
    ]).then(([regularOrdersResult, removalOrdersResult, accountResult, oxfordOrdersResult, timeslotsResult, paymentMethodResult]) => {
      if (!isMounted) return;

      // Merge both types of orders
      const allOrders = [
        ...regularOrdersResult.data.previousOrders,
        ...removalOrdersResult.data.orders
      ];

      // Sort by creation date (most recent first)
      const sortedOrders = allOrders.sort((a, b) => b.createdAt - a.createdAt);
      setOrders(sortedOrders);

      // Merge nextPayments from both responses
      const paymentsMap = new Map<number, NextPaymentResponse>();

      // Handle regular orders nextPayments
      if (regularOrdersResult.data.nextPayments) {
        Object.entries(regularOrdersResult.data.nextPayments).forEach(([key, value]) => {
          paymentsMap.set(Number(key), value);
        });
      }

      // Handle removal orders nextPayments
      if (removalOrdersResult.data.nextPayments) {
        Object.entries(removalOrdersResult.data.nextPayments).forEach(([key, value]) => {
          paymentsMap.set(Number(key), value);
        });
      }

      setNextPayments(paymentsMap);

      // Update totalBoxDays and membershipTier from account data
      const boxDays = accountResult.data.totalBoxDays;
      if (boxDays !== undefined) {
        setTotalBoxDays(boxDays);
      }

      const tier = accountResult.data.membershipTier;
      if (tier !== undefined) {
        setMembershipTier(tier);
      }

      // Save to cache including totalBoxDays and membershipTier
      saveDashboardCache(sortedOrders, paymentsMap, boxDays, tier);

      // Oxford package orders
      if (oxfordOrdersResult.data.orders) {
        setOxfordOrders(oxfordOrdersResult.data.orders);
      }
      if (oxfordOrdersResult.data.jwt) {
        axiosKK.defaults.headers.common['Authorization'] =
          'bearer ' + oxfordOrdersResult.data.jwt;
      }

      if (timeslotsResult.data.timeslots) {
        const ids = new Set(timeslotsResult.data.timeslots.map((ts) => ts.orderId));
        setBookedTimeslotOrderIds(ids);
      }

      setHasPaymentMethod(paymentMethodResult.data.hasPaymentMethod ?? false);

      setIsLoading(false);
    }).catch((error) => {
      if (!isMounted) return;
      console.error('Error fetching dashboard data:', error);
      // If regular orders fail and we have no cache, set empty array
      if (!cachedData) {
        setOrders([]);
      }
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    // Only track dashboard view once when data is loaded
    if (!dashboardViewTracked.current && !isLoading && orders.length >= 0) {
      gtmEventTracking({
        event: 'dashboard_viewed',
        activeOrdersCount: orders.filter(order => !isOrderCompleted(order)).length,
        totalOrdersCount: orders.length
      });
      dashboardViewTracked.current = true;
    }
  }, [orders, isLoading, gtmEventTracking]);

  const activeOrders = orders.filter(order => !isOrderCompleted(order));
  const completedOrders = orders.filter(order => isOrderCompleted(order));
  const displayOrders = activeOrders;

  const eligibleOrdersForReturn = orders.filter(
    (order) =>
      order.storageRequired && !order.suitcaseOrder && isEmptyValue(order.deliveryDate),
  );

  const eligibleOrdersForTimeSlot = activeOrders.filter(
    (order) =>
      order.orderType !== 'removal' &&
      !order.suitcaseOrder &&
      !bookedTimeslotOrderIds.has(order.id),
  );

  const handleReturnItems = () => {
    if (eligibleOrdersForReturn.length > 0) {
      setShowOrderSelectionModal(true);
    } else {
      history.push('/');
    }
  };

  const handleOrderSelection = (order: Order) => {
    setSelectedOrder(order);
    setShowOrderSelectionModal(false);
    setShowDeliveryModal(true);
  };

  const handleDeliveryModalClose = () => {
    setShowDeliveryModal(false);
    setSelectedOrder(null);
  };

  const handleDeliveryUpdate = (updatedOrder: Order) => {
    setOrders((prevOrders) => {
      const updatedOrders = prevOrders.map((order) =>
        order.id === updatedOrder.id ? updatedOrder : order,
      );
      clearDashboardCache();
      return updatedOrders;
    });
    handleDeliveryModalClose();
  };

  const toggleOrderExpansion = (orderId: number) => {
    setExpandedOrderIds(prev => {
      const newSet = new Set(prev);
      const isExpanding = !newSet.has(orderId);

      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }

      gtmEventTracking({
        event: isExpanding ? 'order_expanded' : 'order_collapsed',
        orderId: orderId
      });

      return newSet;
    });
  };

  const handleViewOrderDetails = (orderId: number, orderType?: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    gtmEventTracking({
      event: 'order_details_viewed',
      orderId: orderId,
      orderType: orderType || 'regular'
    });
    if (orderType === 'removal') {
      history.push(`/removals/order/${orderId}`);
    } else {
      history.push(`/order/${orderId}`);
    }
  };

  const handleRequestReturn = (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    gtmEventTracking({
      event: 'return_requested',
      orderId: order.id
    });
    setSelectedOrder(order);
    setShowDeliveryModal(true);
  };

  const handleNavigateToPastOrders = () => {
    history.push({
      pathname: '/past-orders',
      state: {
        allOrders: orders,
        nextPayments: Object.fromEntries(nextPayments),
        isLoading
      }
    });
  };

  return (
    <>
      <div className="dashboard-hero">
        <div className="dashboard-hero__intro">
          <div className="dashboard-title-wrapper">
            <Title>My Keepr</Title>
          </div>
          {user && (
            <p className="grey welcome-message">
              Welcome back, {user?.firstName}
            </p>
          )}
        </div>
        <div className="dashboard-header-container">
          {(membershipTier !== undefined || totalBoxDays !== undefined) && (
            <div className="membership-tier-metric">
              <p className="membership-tier-metric__points">
                Keepr Points: <strong className="points-value">{totalBoxDays || 0}</strong>
                <button
                  className="info-icon-btn"
                  onClick={() => setShowPointsInfo(!showPointsInfo)}
                  onMouseEnter={() => setShowPointsInfo(true)}
                  onMouseLeave={() => setShowPointsInfo(false)}
                  aria-label="Info about Keepr Points"
                >
                  ⓘ
                </button>
              </p>
              {showPointsInfo && (
                <div className="info-tooltip">
                  Beta feature — more rewards coming soon.
                </div>
              )}
              {membershipTier && (
                <p className={`tier-badge tier-${membershipTier.toLowerCase()}`}>
                  <span className="tier-name">
                    {membershipTier.charAt(0).toUpperCase() + membershipTier.slice(1).toLowerCase()}
                  </span>
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {(() => {
        const hasEligibleTimeslot = eligibleOrdersForTimeSlot.length > 0;
        return (
          <button
            className={`timeslot-cta${hasEligibleTimeslot ? '' : ' timeslot-cta--inactive'}`}
            onClick={() => {
              if (!hasEligibleTimeslot) return;
              gtmEventTracking({ event: 'timeslot_cta_clicked' });
              if (!hasPaymentMethod) {
                history.push('/update-payment-method');
                return;
              }
              setShowTimeSlotModal(true);
            }}
            aria-disabled={!hasEligibleTimeslot}
          >
            <span className="timeslot-cta__icon" aria-hidden="true">&#128337;</span>
            <div className="timeslot-cta__text">
              {hasEligibleTimeslot ? (
                <>
                  <strong>
                    {eligibleOrdersForTimeSlot.length === 1
                      ? `Order #${eligibleOrdersForTimeSlot[0].id}: Collection Time-Slot Available`
                      : 'Collection Time-Slot Available'}
                  </strong>
                  <span>Choose your collection time</span>
                </>
              ) : (
                <>
                  <strong>Collection Time-Slots</strong>
                  <span>Available once you have an order ready for collection.</span>
                </>
              )}
            </div>
            <span className="timeslot-cta__arrow" aria-hidden="true">→</span>
          </button>
        );
      })()}

      <div className="active-orders-section">
        {isLoading && displayOrders.length === 0 ? (
          <OrderCardSkeleton />
        ) : displayOrders.length > 0 ? (
          displayOrders.map((order) => {
            const nextPaymentData = nextPayments.get(order.id);
            const isExpanded = expandedOrderIds.has(order.id);
            
            return (
              <ActiveOrderCard
                key={order.id}
                order={order}
                nextPaymentData={nextPaymentData}
                isExpanded={isExpanded}
                onToggle={() => toggleOrderExpansion(order.id)}
                onRequestReturn={order.storageRequired && !order.suitcaseOrder && isEmptyValue(order.deliveryDate)
                  ? (e) => handleRequestReturn(order, e)
                  : undefined}
                onViewDetails={(e) => handleViewOrderDetails(order.id, order.orderType, e)}
              />
            );
          })
        ) : null}
      </div>

      {oxfordOrders.length > 0 && (
        <div className="oxford-orders-section">
          <h3 className="oxford-orders-heading">Oxford Packages</h3>
          <div className="oxford-orders-list">
            {oxfordOrders.map((oxOrder) => (
              <div key={oxOrder.id} className="oxford-order-card">
                <div className="oxford-order-card__header">
                  <span className="oxford-order-card__badge">Oxford Package</span>
                  <span className={`oxford-order-card__status oxford-order-card__status--${oxOrder.paymentStatus}`}>
                    {oxOrder.paymentStatus === 'succeeded' ? 'Paid' : oxOrder.paymentStatus}
                  </span>
                </div>
                <div className="oxford-order-card__body">
                  <div className="oxford-order-card__row">
                    <span className="oxford-order-card__icon">📦</span>
                    <span className="oxford-order-card__label">{oxOrder.packageName} Package</span>
                    <span className="oxford-order-card__value">{toMoney(oxOrder.packagePrice)}</span>
                  </div>
                  <div className="oxford-order-card__row">
                    <span className="oxford-order-card__icon">🎓</span>
                    <span className="oxford-order-card__label">{oxOrder.college}</span>
                  </div>
                  <div className="oxford-order-card__row">
                    <span className="oxford-order-card__icon">📅</span>
                    <span className="oxford-order-card__label">
                      Booked {Moment(oxOrder.createdAt).format('D MMMM YYYY')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="dashboard-all-box-container customer-dashboard">
        <section onClick={() => {
          gtmEventTracking({ event: 'new_booking_clicked' });
          history.push('/');
        }} className="each-box">
          <div className="content">
            <span className="action-icon">📦</span>
            <h3>Make New Bookings!</h3>
          </div>
          <button className="dashboard-action-btn">Book Now</button>
        </section>
        <section onClick={() => {
          gtmEventTracking({ event: 'profile_clicked' });
          history.push('/profile');
        }} className="each-box">
          <div className="content">
            <span className="action-icon">👤</span>
            <h3>Name, Address and Payment Details</h3>
          </div>
          <button className="dashboard-action-btn">Manage Profile</button>
        </section>
        <section onClick={() => {
          gtmEventTracking({ event: 'payments_clicked' });
          history.push('/payments');
        }} className="each-box">
          <div className="content">
            <span className="action-icon">💳</span>
            <h3>Recurring Payments and Transactions</h3>
          </div>
          <button className="dashboard-action-btn">Track My Payments</button>
        </section>
        {eligibleOrdersForReturn.length > 0 && (
          <section onClick={() => {
            gtmEventTracking({ event: 'return_items_clicked' });
            handleReturnItems();
          }} className="each-box">
            <div className="content">
              <span className="action-icon">↩️</span>
              <h3>Request Return of Your Items</h3>
            </div>
            <button className="dashboard-action-btn">Return My Items</button>
          </section>
        )}
        <section onClick={() => {
          gtmEventTracking({ event: 'extra_services_clicked' });
          setShowExtraServicesModal(true);
        }} className="each-box">
          <div className="content">
            <span className="action-icon">✨</span>
            <h3>Extra services...</h3>
          </div>
          <button className="dashboard-action-btn">View Services</button>
        </section>
        {completedOrders.length > 0 && (
          <section onClick={() => {
            gtmEventTracking({ event: 'past_orders_clicked' });
            handleNavigateToPastOrders();
          }} className="each-box">
            <div className="content">
              <span className="action-icon">📋</span>
              <h3>Past orders</h3>
            </div>
            <button className="dashboard-action-btn">View Past Orders</button>
          </section>
        )}
        <section onClick={() => {
          gtmEventTracking({ event: 'update_payment_clicked' });
          history.push('/update-payment-method');
        }} className="each-box">
          <div className="content">
            <span className="action-icon">🔄</span>
            <h3>Update Payment Method</h3>
          </div>
          <button className="dashboard-action-btn">Update Payment</button>
        </section>
        <section onClick={() => {
          gtmEventTracking({ event: 'faq_clicked' });
          window.open('https://kitkeeper.tawk.help/', '_blank', 'noopener,noreferrer');
        }} className="each-box">
          <div className="content">
            <span className="action-icon">❓</span>
            <h3>Knowledge Base &amp; FAQs</h3>
          </div>
          <button className="dashboard-action-btn">Visit FAQs</button>
        </section>
      </div>

      <div className="line" />

      <OrderSelectionModal
        isOpen={showOrderSelectionModal}
        orders={orders}
        onSelectOrder={handleOrderSelection}
        onClose={() => setShowOrderSelectionModal(false)}
      />

      {selectedOrder && (
        <OrderEditDeliveryDetailsModal
          editMode={showDeliveryModal}
          order={selectedOrder}
          close={handleDeliveryModalClose}
          update={handleDeliveryUpdate}
        />
      )}

      <ExtraServicesModal
        isOpen={showExtraServicesModal}
        onClose={() => setShowExtraServicesModal(false)}
      />

      <TimeSlotModal
        show={showTimeSlotModal}
        close={() => setShowTimeSlotModal(false)}
        orders={eligibleOrdersForTimeSlot}
        onSuccess={(bookedOrderId) => {
          gtmEventTracking({ event: 'timeslot_booked', orderId: bookedOrderId });
          setBookedTimeslotOrderIds((prev) => new Set(prev).add(bookedOrderId));
          setShowTimeSlotModal(false);
        }}
        onNoPaymentMethod={() => {
          setShowTimeSlotModal(false);
          history.push('/update-payment-method');
        }}
      />
    </>
  );
}

export default withRouter(DashboardComponent);

